import type { ContractAiRevisionRunSummary } from "@closer/shared";
import {
  CLAUSE_CHANGE_NAMES,
  CONTRACT_AI_COMPARE_ADVISORY,
  CONTRACT_AI_REVISION_CONTRADICTION_WARNING,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, useToast } from "@closer/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * AI FARK AÇIKLAMASI (CLOS-207 `[190]` · CLOS-1007).
 *
 * ### ⚠ AÇIKLAMA, FARKIN YERİNE DEĞİL YANINA
 *
 * Kart: *"`Deterministik fark` ile `AI açıklaması` ayrı başlık/etiket taşır."* Bu bileşen
 * karşılaştırma sayfasının ALTINDA duruyor ve kendi başlığını taşıyor; farkın kendisini
 * hiç çizmiyor. Açıklamayı madde kartlarının içine karıştırsaydık, kullanıcı hangisinin
 * hesaplanmış hangisinin yorum olduğunu ayırt edemezdi.
 *
 * ### ⚠ ÇELİŞEN SATIR AYRI İŞARETLENİYOR
 *
 * Sunucu çelişen açıklama satırlarının indekslerini döndürüyor (deterministik olarak
 * hesaplanmış). Ekran o satırları uyarıyla gösteriyor ve cümlenin kendisini SİLMİYOR:
 * kullanıcı modelin ne dediğini de görmeli, ama neye güveneceğini bilerek.
 */
export function ContractCompareExplanation({
  contractId,
  leftVersionId,
  rightVersionId,
}: {
  contractId: string;
  leftVersionId: string;
  rightVersionId: string;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [calisma, setCalisma] = useState<ContractAiRevisionRunSummary | null>(null);

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const acikla = useMutation({
    mutationFn: async () =>
      (
        await api<ContractAiRevisionRunSummary>(
          `/api/contracts/${contractId}/ai-compare-explanations`,
          {
            method: "POST",
            tenantId,
            body: JSON.stringify({ leftVersionId, rightVersionId }),
            headers: { "idempotency-key": idempotency.uret() },
          },
        )
      ).data,
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
      setCalisma(sonuc);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Açıklama üretilemedi"),
  });

  /** ⚠ Sunucu kapısı `contract.view`; karşılaştırmayı gören açıklamayı da isteyebilir. */
  if (!can("contract.view")) return null;

  const aciklama = calisma?.explanation ?? null;
  const celisenler = new Set(calisma?.contradictions ?? []);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">AI açıklaması</h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={acikla.isPending}
          disabled={acikla.isPending}
          onClick={() => acikla.mutate()}
        >
          {acikla.isPending ? "Açıklanıyor…" : aciklama ? "Yeniden açıkla" : "Farkı AI ile açıkla"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      {/* ⚠ Kartın zorunlu cümlesi: açıklama farkın kendisi değil. */}
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-fark-uyari">
        {CONTRACT_AI_COMPARE_ADVISORY}
      </p>

      {aciklama ? (
        <div className="mt-4 flex flex-col gap-3" data-testid="ai-fark-sonuc">
          <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            <Badge tone="neutral">{calisma!.model}</Badge>
            <span className="tabular-nums">{calisma!.credits} kredi</span>
            {/* ⚠ Fark sonucu değiştiyse açıklama başka bir farka ait. */}
            {calisma!.stale ? <Badge tone="warning">Bu fark artık geçerli değil</Badge> : null}
          </div>

          {aciklama.summary ? <p className="text-sm text-fg">{aciklama.summary}</p> : null}

          {celisenler.size > 0 ? (
            <InfoBanner tone="warning">
              <span data-testid="ai-fark-celiski">
                {CONTRACT_AI_REVISION_CONTRADICTION_WARNING}
              </span>
            </InfoBanner>
          ) : null}

          <ul className="flex flex-col gap-2">
            {aciklama.notes.map((not, i) => (
              <li
                key={i}
                data-testid={celisenler.has(i) ? "ai-fark-celisen-satir" : "ai-fark-satir"}
                className={
                  celisenler.has(i)
                    ? "rounded-lg border border-warning/50 bg-warning-soft/40 p-3"
                    : "rounded-lg border border-border-subtle p-3"
                }
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={celisenler.has(i) ? "warning" : "neutral"}>
                    {CLAUSE_CHANGE_NAMES[not.change]}
                  </Badge>
                  {celisenler.has(i) ? (
                    <span className="text-xs text-warning">Hesaplanan farkla örtüşmüyor</span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-fg">{not.explanation}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
